// src/components/digital_twin/Pipeline3DLayer.jsx
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   PERFORMANCE
//   • The page passed `onLayerUpdated` as an inline arrow, so every page render (every live
//     telemetry flush) disposed and rebuilt every pipe tube. The callback is now read through a
//     ref, and the scene is only rebuilt when the pipelines' geometry / status / simulation
//     actually change (content signature), not when a new array with the same data arrives.
//   FIXES
//   • "warning" status (used by the Pump Failure mode) had no colour and rendered as blue.
//   • The simulation names compared exactly ('Pipe Burst'); scenario titles such as
//     "Major Pipe Burst" never matched. Now matched by keyword (burst / pump).
//   • path_coordinates as a JSON string, [lng, lat] pairs or {latitude, longitude} were skipped.
//   • Meshes were left in the scene after the component unmounted; they are disposed.
//   FEATURES
//   • `visible` prop (layer toggle without a rebuild), `liveStatus` map {pipelineId: status}
//     for sensor-driven colours, `highlightId` for the selected pipe, `onBuilt(count)`.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { MercatorCoordinate } from 'mapbox-gl';

// ==================== DEBUG LOGGER ====================
const logger = {
  log: (...args) => console.log('[PIPELINE_LAYER]', ...args),
  warn: (...args) => console.warn('[PIPELINE_LAYER]', ...args),
  error: (...args) => console.error('[PIPELINE_LAYER]', ...args),
};

// ==================== CONSTANTS & MATERIALS ====================
const statusColors = {
  operational: 0x00dd00, // Green
  maintenance_required: 0xffa500, // Orange
  damaged: 0xff0000, // Red
  critical: 0xff0000, // Red
  offline: 0x808080, // Gray
  unknown: 0x00aaff, // Default Blue
  // --- UPDATE
  warning: 0xffa500, // Orange (Pump Failure mode / live warning)
  normal: 0x00dd00,
  active: 0x00dd00,
  maintenance: 0xffa500,
  // --- END UPDATE
};

// Reusable material generator for high-performance rendering
const createPipelineMaterial = (hexColor, isCritical = false) => {
  return new THREE.MeshPhysicalMaterial({
    color: hexColor,
    metalness: 0.2,
    roughness: 0.3,
    clearcoat: 0.5,
    transparent: true,
    opacity: 0.85,
    emissive: hexColor,
    emissiveIntensity: isCritical ? 0.8 : 0.2, // Glow if critical
  });
};

// --- UPDATE: helpers
/** Accepts [{lng,lat}], [{longitude,latitude}], [[lng,lat]] or a JSON string of either. */
export function normalizePath(raw) {
  let p = raw;
  if (typeof p === 'string') { try { p = JSON.parse(p); } catch (_) { return []; } }
  if (p && p.type === 'LineString' && Array.isArray(p.coordinates)) p = p.coordinates;
  if (!Array.isArray(p)) return [];
  return p.map((c) => {
    if (Array.isArray(c)) return { lng: Number(c[0]), lat: Number(c[1]) };
    if (c && typeof c === 'object') return { lng: Number(c.lng ?? c.lon ?? c.longitude), lat: Number(c.lat ?? c.latitude) };
    return null;
  }).filter((c) => c && Number.isFinite(c.lng) && Number.isFinite(c.lat) && Math.abs(c.lat) <= 90 && Math.abs(c.lng) <= 180);
}
export function simulationStatusFor(simulationMode, currentStatus) {
  const m = String(simulationMode || '').toLowerCase();
  if (!m) return currentStatus;
  if (m.includes('burst')) return 'damaged';
  if (m.includes('pump') && currentStatus !== 'damaged') return 'warning';
  return currentStatus;
}
const disposeChild = (child) => {
  if (child.geometry) child.geometry.dispose();
  if (child.material) (Array.isArray(child.material) ? child.material : [child.material]).forEach((m) => m.dispose());
};
// --- END UPDATE

const Pipeline3DLayer = ({
  scene,
  origin,
  pipelines = [],
  simulationMode = null,
  onLayerUpdated,
  // --- UPDATE
  visible = true,
  liveStatus = null,
  highlightId = null,
  onBuilt = null,
  // --- END UPDATE
}) => {
  const groupRef = useRef(null);
  // --- UPDATE: callbacks via refs (no rebuild when the parent passes a new function)
  const onLayerUpdatedRef = useRef(onLayerUpdated);
  onLayerUpdatedRef.current = onLayerUpdated;
  const onBuiltRef = useRef(onBuilt);
  onBuiltRef.current = onBuilt;
  const signatureRef = useRef(null);
  const signature = React.useMemo(() => {
    const live = liveStatus ? JSON.stringify(Object.keys(liveStatus).sort().map((k) => [k, liveStatus[k]])) : '';
    return `${(pipelines || []).map((p) => `${p.id}:${p.status}:${p.diameter_inches}:${typeof p.path_coordinates === 'string' ? p.path_coordinates.length : (p.path_coordinates || []).length}:${JSON.stringify((normalizePath(p.path_coordinates)[0]) || '')}`).join('|')}#${simulationMode || ''}#${live}#${highlightId ?? ''}`;
  }, [pipelines, simulationMode, liveStatus, highlightId]);
  // --- END UPDATE

  useEffect(() => {
    if (!scene || !origin) return;
    // --- UPDATE: skip identical rebuilds
    if (signatureRef.current === signature && scene.getObjectByName('pipelines')?.children.length) return;
    signatureRef.current = signature;
    // --- END UPDATE

    // 1. Ensure/Get the Pipelines Group
    let group = scene.getObjectByName('pipelines');
    if (!group) {
      group = new THREE.Group();
      group.name = 'pipelines';
      scene.add(group);
    }
    groupRef.current = group;

    // 2. Cleanup existing pipelines from THIS component's memory
    while (group.children.length > 0) {
      const child = group.children[0];
      // --- UPDATE: also disposes material arrays
      disposeChild(child);
      group.remove(child);
    }

    if (pipelines.length === 0) {
      if (onLayerUpdatedRef.current) onLayerUpdatedRef.current();
      return;
    }

    // 3. Base scale calculations (Crucial for Mapbox 3D)
    const meterInUnits = origin.meterInMercatorCoordinateUnits();

    // FIX: Set Z offset to ~1.5 meters above the map to prevent clipping/z-fighting
    const zOffset = 1.5 * meterInUnits;
    let built = 0; // --- UPDATE

    // 4. Build 3D Tubes for each pipeline
    pipelines.forEach(pipeline => {
      // --- UPDATE: accept every coordinate shape
      const path = normalizePath(pipeline.path_coordinates);
      if (path.length < 2) {
        logger.warn(`Skipping pipeline ${pipeline.id}: Invalid path_coordinates`);
        return;
      }
      // --- END UPDATE

      try {
        // Convert Lat/Lng to Mercator Vectors relative to the scene origin
        const points = path.map(coord => {
          const mc = MercatorCoordinate.fromLngLat([coord.lng, coord.lat]);
          return new THREE.Vector3(
            mc.x - origin.x,
            mc.y - origin.y,
            zOffset
          );
        });

        // Create a smooth 3D curve through the points
        const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.2);

        // Calculate dynamic tube radius
        // FIX: Ensure pipes are visually exaggerated to at least 1.5 meters thick so they are visible from high zoom
        const baseRadiusMeters = ((pipeline.diameter_inches || 12) * 0.0254) / 2;
        const visualRadiusMeters = Math.max(baseRadiusMeters * 5, 1.5);
        const tubeRadius = visualRadiusMeters * meterInUnits * (String(pipeline.id) === String(highlightId) ? 1.6 : 1); // --- UPDATE: selected pipe thicker

        // Generate the 3D Tube Geometry
        const geometry = new THREE.TubeGeometry(
          curve,
          Math.min(points.length * 8, 512), // tubular segments (higher = smoother corners) — --- UPDATE: capped for very long pipes
          tubeRadius,
          12, // radial segments (smooth round pipe)
          false // closed
        );

        // Determine Status & Color
        let currentStatus = pipeline.status?.toLowerCase().replace(/\s+/g, '_') || 'unknown';

        if (simulationMode === 'Pipe Burst') currentStatus = 'damaged';
        if (simulationMode === 'Pump Failure' && currentStatus !== 'damaged') currentStatus = 'warning';
        // --- UPDATE: scenario titles by keyword + live sensor status (worst wins)
        currentStatus = simulationStatusFor(simulationMode, currentStatus);
        const live = liveStatus ? liveStatus[String(pipeline.id)] : null;
        if (!simulationMode && live === 'critical') currentStatus = 'critical';
        else if (!simulationMode && live === 'warning' && !['damaged', 'critical'].includes(currentStatus)) currentStatus = 'warning';
        // --- END UPDATE

        const isCritical = ['damaged', 'critical'].includes(currentStatus);
        const pipeColor = statusColors[currentStatus] || statusColors.unknown;

        // Apply Premium Material
        const material = createPipelineMaterial(pipeColor, isCritical);
        const tubeMesh = new THREE.Mesh(geometry, material);

        // Attach metadata for the Raycaster (Clicking/Hovering)
        tubeMesh.userData = {
          type: 'pipeline',
          pipelineId: pipeline.id,
          sourceData: pipeline,
          originalScale: 1,
          status: currentStatus, // --- UPDATE
        };
        tubeMesh.name = `pipeline-${pipeline.id}`;

        // Add to the group
        group.add(tubeMesh);
        built += 1; // --- UPDATE

      } catch (err) {
        logger.error(`Failed to render pipeline ${pipeline.id}:`, err);
      }
    });

    // Notify parent that new objects exist so raycaster can find them
    // --- UPDATE: through refs
    if (onLayerUpdatedRef.current) {
      onLayerUpdatedRef.current();
    }
    onBuiltRef.current?.(built);
    // --- END UPDATE

  }, [scene, origin, pipelines, simulationMode, signature, highlightId, liveStatus]); // --- UPDATE: onLayerUpdated read via ref

  // --- UPDATE: visibility without a rebuild
  useEffect(() => {
    const group = scene?.getObjectByName('pipelines');
    if (group) group.visible = !!visible;
  }, [scene, visible, signature]);

  // --- UPDATE: dispose on unmount
  useEffect(() => () => {
    const group = groupRef.current;
    if (!group) return;
    while (group.children.length > 0) { const c = group.children[0]; disposeChild(c); group.remove(c); }
    signatureRef.current = null;
  }, []);
  // --- END UPDATE

  return null;
};

export default Pipeline3DLayer;
